import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import {
  type FilterDefinition,
  FiltersProvider,
  useFilters,
} from '@spotify/confidence-core-react';
import { AdminLabelsV1LabelResourceType } from '@spotify/confidence-graphql';

import { LabelDefinition, useLabelsForResource } from '../../hooks';
import {
  LABEL_FILTER_ID,
  createGroupedLabelFilter,
  formatLabelValue,
} from './LabelFilter';

type LabelFiltersContextValue = {
  labelDefinitions: LabelDefinition[];
  addLabelFilter: (labelKey: string, value: string) => void;
};

const LabelFiltersContext = createContext<LabelFiltersContextValue>({
  labelDefinitions: [],
  addLabelFilter: () => {},
});

type FiltersProviderWithLabelsProps = {
  filters?: FilterDefinition[];
  storageKey: string;
  children: ReactNode;
  searchField: string;
  defaultFilters?: Array<{ id: string; value: unknown }>;
  resourceType: AdminLabelsV1LabelResourceType;
  disableQueryParams?: boolean;
};

export function FiltersProviderWithLabels({
  filters = [],
  storageKey,
  children,
  searchField,
  defaultFilters = [],
  resourceType,
  disableQueryParams,
}: FiltersProviderWithLabelsProps) {
  const { labelDefinitions } = useLabelsForResource({ resourceType });

  const labelFilter = useMemo(
    () =>
      labelDefinitions.length > 0
        ? createGroupedLabelFilter(labelDefinitions)
        : undefined,
    [labelDefinitions],
  );

  const allFilters = useMemo(
    () => (labelFilter ? [...filters, labelFilter] : filters),
    [filters, labelFilter],
  );

  return (
    <FiltersProvider
      filters={allFilters}
      storageKey={storageKey}
      searchField={searchField}
      defaultFilters={defaultFilters}
      disableQueryParams={disableQueryParams}
    >
      <LabelFiltersContextProvider
        labelDefinitions={labelDefinitions}
        labelFilter={labelFilter}
      >
        {children}
      </LabelFiltersContextProvider>
    </FiltersProvider>
  );
}

function LabelFiltersContextProvider({
  labelDefinitions,
  labelFilter,
  children,
}: {
  labelDefinitions: LabelDefinition[];
  labelFilter: FilterDefinition | undefined;
  children: ReactNode;
}) {
  const { addFilter, activeFilters } = useFilters();

  const activeLabelValues =
    (activeFilters.find(f => f.id === LABEL_FILTER_ID)?.value as
      | string[]
      | undefined) ?? [];

  const addLabelFilter = useCallback(
    (labelKey: string, value: string) => {
      if (!labelFilter) return;
      const composite = formatLabelValue(labelKey, value);
      if (!activeLabelValues.includes(composite)) {
        addFilter(labelFilter, [...activeLabelValues, composite]);
      }
    },
    [labelFilter, addFilter, activeLabelValues],
  );

  const contextValue = useMemo(
    () => ({
      labelDefinitions,
      addLabelFilter,
    }),
    [labelDefinitions, addLabelFilter],
  );

  return (
    <LabelFiltersContext.Provider value={contextValue}>
      {children}
    </LabelFiltersContext.Provider>
  );
}

export function useLabelFilters() {
  return useContext(LabelFiltersContext);
}
